import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";

// What the startup loading bar shows.
//
// The bar used to be "images loaded / images total": it reached 100% once the
// art was decoded and then sat there while login, configs and the menu were
// still on their way, which reads as a hang. Now:
//
//  - Real progress is a weighted sum of the three things startup waits for:
//    critical art, the network bootstrap steps and building the first screen.
//  - The shown value follows real progress quickly and, while real progress
//    stalls, keeps creeping into the remaining gap ever slower, so the number
//    never freezes; it is capped at CAP until the first screen is ready.
//  - 100% is reached only in finish(): a quick fill right before the loader
//    goes away, so "100%" always means "now".

type NetStep = "login" | "bootstrap" | "configs" | "events" | "levels";
const NET_STEPS: ReadonlyArray<NetStep> = [ "login", "bootstrap", "configs", "events", "levels" ];

type ScreenStage = "request" | "startup_ready" | "loader_painted" | "resources_ready" | "level_ready" | "content_shown" | "scene_revealed" | "loader_hidden";

// Fraction of the screen phase each stage stands for; a level has far more to
// build than the menu (3D models, the level itself, the first scene render).
const GAME_STAGES: Record<ScreenStage, number> = {
  request: 0.05, startup_ready: 0.1, loader_painted: 0.15, resources_ready: 0.6,
  level_ready: 0.7, content_shown: 0.9, scene_revealed: 1, loader_hidden: 1,
};
const MENU_STAGES: Record<ScreenStage, number> = {
  request: 0.3, startup_ready: 0.3, loader_painted: 0.3, resources_ready: 0.3,
  level_ready: 0.3, content_shown: 1, scene_revealed: 1, loader_hidden: 1,
};

const W_ASSETS = 0.45;
const W_NET = 0.4;
const W_SCREEN = 0.15;
// A first level also loads the 3D models, so the screen phase weighs more.
const W_SCREEN_GAME = 0.3;

const CAP = 0.95;
const FOLLOW_TAU_S = 0.25;
const CREEP_TAU_S = 3;
const CREEP_SHARE = 0.5;
const FINISH_TAU_S = 0.05;
// Each font/audio/localization promise counts like this many images.
const EXTRA_UNIT = 8;

export class StartupProgress
{
  private static asset_ids: ImgID[] = [];
  private static asset_extras_total = 0;
  private static asset_extras_done = 0;
  private static net = new Set<NetStep>();
  private static screen = 0;
  private static screen_is_game = false;
  private static finished = false;
  private static shown = 0;
  private static last_now = 0;

  /** The critical startup set; progress counts how many of these are decoded. */
  public static trackAssets( ids: ImgID[], extras: Promise<unknown>[] ): void
  {
    this.asset_ids = ids;
    this.asset_extras_total = extras.length;
    this.asset_extras_done = 0;
    for ( const p of extras )
      void p.then( () => { this.asset_extras_done++; }, () => {} );
  }

  /** Known from the start (a first-time player goes straight into level 1); sets the phase weights. */
  public static setFirstScreenIsGame( is_game: boolean ): void
  {
    this.screen_is_game = is_game;
  }

  public static netStep( step: NetStep ): void
  {
    this.net.add( step );
  }

  /** Offline start or a startup that skips the network: the phase is done. */
  public static netAll(): void
  {
    for ( const step of NET_STEPS )
      this.net.add( step );
  }

  public static screenStage( is_game: boolean, stage: ScreenStage ): void
  {
    if ( this.finished )
      return;
    const value = ( is_game ? GAME_STAGES : MENU_STAGES )[stage];
    this.screen = Math.max( this.screen, value );
  }

  public static finish(): void
  {
    this.finished = true;
  }

  public static isFinished(): boolean
  {
    return this.finished;
  }

  public static shownValue(): number
  {
    return this.shown;
  }

  public static real(): number
  {
    let assets = 0;
    const units = this.asset_ids.length + this.asset_extras_total * EXTRA_UNIT;
    if ( units > 0 )
    {
      let ready = this.asset_extras_done * EXTRA_UNIT;
      for ( const id of this.asset_ids )
        if ( ImagesHolder.isReady( id ) ) ready++;
      assets = ready / units;
    }

    const net = this.net.size / NET_STEPS.length;

    let screen = this.screen;
    if ( this.screen_is_game && screen >= GAME_STAGES.loader_painted && screen < GAME_STAGES.resources_ready )
    {
      const total = ResourceManager.getTotalCount();
      const models = total > 0 ? ResourceManager.getDownloadedCount() / total : 0;
      const span = GAME_STAGES.resources_ready - GAME_STAGES.loader_painted;
      screen = Math.max( screen, GAME_STAGES.loader_painted + span * models );
    }

    const w_screen = this.screen_is_game ? W_SCREEN_GAME : W_SCREEN;
    const scale = 1 / ( W_ASSETS + W_NET + w_screen );
    return ( W_ASSETS * assets + W_NET * net + w_screen * screen ) * scale;
  }

  /** Advances the shown value to `now` and returns it. */
  public static update( now: number ): number
  {
    // Up to half a second per frame: startup frames can be long (decoding,
    // layout) and the bar must not fall behind real progress because of it.
    const dt_s = this.last_now > 0 ? Math.min( 0.5, ( now - this.last_now ) / 1000 ) : 0;
    this.last_now = now;

    if ( this.finished )
    {
      this.shown += ( 1 - this.shown ) * ( 1 - Math.exp( -dt_s / FINISH_TAU_S ) );
      if ( this.shown > 0.995 ) this.shown = 1;
      return this.shown;
    }

    const real = Math.min( CAP, this.real() );
    let next: number;
    if ( this.shown < real )
      next = this.shown + ( real - this.shown ) * ( 1 - Math.exp( -dt_s / FOLLOW_TAU_S ) );
    else
    {
      const ceiling = real + ( CAP - real ) * CREEP_SHARE;
      next = this.shown + ( ceiling - this.shown ) * ( 1 - Math.exp( -dt_s / CREEP_TAU_S ) );
    }
    // Never backwards: real progress can dip when a first level turns out to
    // weigh the screen phase more than the menu would.
    this.shown = Math.min( CAP, Math.max( this.shown, next ) );
    return this.shown;
  }
}
